/**
 * Types communs pour le système de providers modulaire
 * 
 * Ce fichier définit les types et interfaces pour gérer plusieurs fournisseurs
 * de devices et de SIM de manière modulaire (Knox, Strapp, etc.)
 */

/**
 * @typedef {Object} Device
 * @property {string} id - Identifiant unique du device
 * @property {string} provider - Nom du provider (knox, strapp, etc.)
 * @property {string} imei - Numéro IMEI
 * @property {string} [serial] - Numéro de série
 * @property {string} [model] - Modèle du device
 * @property {string} status - Statut du device
 * @property {Object} [user] - Utilisateur associé
 * @property {Object} [site] - Site associé
 * @property {Date} [lastSync] - Dernière synchronisation
 * @property {Object} raw - Données brutes du provider (pour accès spécifique)
 */

/**
 * @typedef {Object} Sim
 * @property {string} iccid - Identifiant unique de la SIM (ICCID)
 * @property {string} provider - Nom du provider (strapp, etc.)
 * @property {string} [msisdn] - Numéro de téléphone
 * @property {string} status - Statut de la SIM (active, inactive, terminated, etc.)
 * @property {string} [operator] - Opérateur mobile
 * @property {Object} [usage] - Données de consommation
 * @property {number} [usage.totalDataMo] - Consommation totale en Mo
 * @property {number} [usage.periodDataMo] - Consommation de la période
 * @property {number} [quotaMo] - Quota mensuel en Mo
 * @property {string} [assignedDevice] - IMEI du device associé
 * @property {Date} [activationDate] - Date d'activation
 * @property {Date} [lastSync] - Dernière synchronisation
 * @property {Object} raw - Données brutes du provider
 */

/**
 * @typedef {Object} DeviceFilters
 * @property {string} [id] - Filtrer par ID
 * @property {string} [imei] - Filtrer par IMEI
 * @property {string} [serial] - Filtrer par serial
 * @property {Array<string>} [statuses] - Filtrer par statuts
 * @property {string} [userId] - Filtrer par utilisateur
 * @property {string} [siteId] - Filtrer par site
 * @property {boolean} [force] - Forcer le refresh cache
 * @property {number} [limit] - Limite de résultats
 * @property {number|string} [cursor] - Curseur de pagination
 */

/**
 * @typedef {Object} SimFilters
 * @property {string} [iccid] - Filtrer par ICCID
 * @property {string} [msisdn] - Filtrer par numéro
 * @property {string} [userId] - Filtrer par utilisateur
 * @property {Array<string>} [statuses] - Filtrer par statuts
 * @property {string} [assignedDevice] - Filtrer par device assigné
 * @property {boolean} [force] - Forcer le refresh cache
 * @property {number} [limit] - Limite de résultats
 * @property {number|string} [cursor] - Curseur de pagination
 */

/**
 * @typedef {Object} PaginatedResult
 * @property {Array} data - Les résultats
 * @property {number|string} [cursor] - Curseur pour la page suivante
 * @property {number} [remaining] - Nombre d'éléments restants
 * @property {number} [total] - Nombre total d'éléments
 */

/**
 * Interface pour un provider de devices
 * Chaque fournisseur de tablettes (Knox, Strapp, etc.) doit implémenter cette interface
 * 
 * @typedef {Object} IDeviceProvider
 * @property {string} name - Nom unique du provider
 * @property {Function} getDevices - Récupère une liste de devices
 * @property {Function} getDeviceById - Récupère un device par son ID
 * @property {Function} getDeviceByImei - Récupère un device par son IMEI
 * @property {Function} [normalizeDevice] - Normalise les données du provider vers le format commun
 */

/**
 * Interface pour un provider de SIM
 * Chaque fournisseur de SIM (Strapp, etc.) doit implémenter cette interface
 * 
 * @typedef {Object} ISimProvider
 * @property {string} name - Nom unique du provider
 * @property {Function} getSims - Récupère une liste de SIMs
 * @property {Function} getSimByIccid - Récupère une SIM par son ICCID
 * @property {Function} [activateSim] - Active une SIM
 * @property {Function} [deactivateSim] - Désactive une SIM
 * @property {Function} [getSimUsage] - Récupère la consommation d'une SIM
 * @property {Function} [normalizeSim] - Normalise les données du provider vers le format commun
 */

/**
 * Statuts standardisés pour les devices
 */
export const DeviceStatus = {
  ACTIVE: 'active',
  INACTIVE: 'inactive',
  PENDING: 'pending',
  BLOCKED: 'blocked',
  RETIRED: 'retired'
}

/**
 * Statuts standardisés pour les SIM
 */
export const SimStatus = {
  ACTIVE: 'active',
  INACTIVE: 'inactive',
  SUSPENDED: 'suspended',
  TERMINATED: 'terminated',
  PENDING: 'pending'
}

/**
 * Mapping des statuts Knox vers les statuts standardisés
 */
export const KnoxStatusMapping = {
  'I': DeviceStatus.INACTIVE,
  'P': DeviceStatus.PENDING,
  'A': DeviceStatus.ACTIVE,
  'B': DeviceStatus.BLOCKED,
  'BS': DeviceStatus.BLOCKED,
  'BA': DeviceStatus.BLOCKED,
  'BL': DeviceStatus.BLOCKED
}

/**
 * Mapping des statuts Strapp vers les statuts standardisés
 */
export const StrappStatusMapping = {
  'in_stock': DeviceStatus.INACTIVE,
  'assigned': DeviceStatus.ACTIVE,
  'in_use': DeviceStatus.ACTIVE,
  'maintenance': DeviceStatus.INACTIVE,
  'retired': DeviceStatus.RETIRED
}
